import { Carousel } from '@mantine/carousel'
import { Box, Divider, Stack, Text, Title, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconCaretLeftFilled, IconCaretRightFilled } from '@tabler/icons-react'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAuth } from '@/plugins/auth'
import ConnectMiddleware from '@/plugins/auth/RouteRedirectors/ConnectMiddleware'
import { useDb } from '@/plugins/db/Db'

import useSpaceRegistry from '../../hooks/useSpaceRegistry'
import { BodyStack } from '../../styled/components'
import ErrorMessage from '../ErrorMessage'
import HelpDrawer from '../HelpDrawer/HelpDrawer'
import Loading from '../Loading'
import Drafts from './Drafts'

const Component: React.FC = () => {
  const { t } = useTranslation()
  const { status, address } = useAuth()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const { db } = useDb()
  const [draftsTableOnDb, setDraftsTableOnDb] = useState<boolean>(false)
  const { loading, error, spacesMetadata, getTemplatesBySpaces } = useSpaceRegistry()

  // Avoid rendering Drafts component if the drafts table does not exist
  const checkDraftsTableOnDb = useCallback(
    async () =>
      (db &&
        status === 'connected' &&
        address &&
        db.tables.some(table => table.name.includes('drafts'))) ||
      false,
    [db, status, address]
  )

  useEffect(() => {
    checkDraftsTableOnDb().then(setDraftsTableOnDb)
  }, [checkDraftsTableOnDb])

  return (
    <ConnectMiddleware>
      <BodyStack>
        <Stack style={{ gap: '25px', textAlign: 'left' }}>
          <Title order={isMobile ? 3 : 2}>
            {t('components.studio.title', 'Welcome to the Creator Studio')}
          </Title>
          <Stack style={{ gap: '2px' }}>
            <Text>
              {t(
                'components.studio.subtitle1',
                'Here you can publish items to the LAMINA1 and define how they will appear, integrate, and be engaged with across the LAMINA1 Hub and Ecosystem. '
              )}
            </Text>
            <Text>
              {t(
                'components.studio.subtitle2',
                'Get started by selecting a template for publishing your item below.'
              )}
            </Text>
          </Stack>
          <Divider />
        </Stack>
        <Stack style={{ margin: '30px 0', textAlign: 'left' }}>
          <Title order={3}>{t('components.studio.templates', 'Templates')}</Title>
          <Text>
            {t(
              'components.studio.templateSubtitle',
              'Templates are pre-set flows that help you publish items. More file formats and template types coming soon.'
            )}
          </Text>
          {error && <ErrorMessage message={error.message} details={error.details} />}
          {!error && !loading && spacesMetadata && (
            <Stack style={{ alignItems: 'center' }}>
              <Carousel
                key="spaces-templates"
                slideSize="15%"
                previousControlIcon={<IconCaretLeftFilled />}
                nextControlIcon={<IconCaretRightFilled />}
              >
                {Object.entries(spacesMetadata)
                  .filter(([, value]) => value.active && value.metadataInfo)
                  .map(([key, value]) => {
                    const template = getTemplatesBySpaces(key, value)
                    if (template && template.length > 0) {
                      return <Carousel.Slide key={key}>{template}</Carousel.Slide>
                    }
                    return null
                  })
                  .filter(template => template !== null)}
              </Carousel>
            </Stack>
          )}
          {!error && loading && (
            <Box
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '300px',
                width: '300px',
              }}
            >
              <Loading />
            </Box>
          )}
        </Stack>
        {draftsTableOnDb && <Drafts />}
      </BodyStack>
      <HelpDrawer id="Studio" />
    </ConnectMiddleware>
  )
}
Component.displayName = 'Studio'
// eslint-disable-next-line import/prefer-default-export
export { Component }
